import { FC, useState } from 'react';
import { PublishMenusModalProps } from '../PublishMenus.types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Autocomplete, TextField } from '@mui/material';
import Modal from '../../../components/Modal/Modal';
import { Buttons, Message } from '../../../common/TwoButtonModal/TwoButtonModal.styles';
import rem from '../../../utils/rem/rem';
import Button from '../../../components/Button/Button';
import { Variant } from '../../../components/Button/Button.types';
import Table from '../../../components/Table/Table';
import { MessagesWrapper, StyledMessage, StyledSubMessage } from '../SelectStores/SelectStores.styles';

const PublishMenusModal: FC<PublishMenusModalProps> = ({
  open,
  onClose,
  isLoading,
  onPublish,
  selectedStores,
  setSelectedChannels,
  selectedChannels,
  menuChannels,
}) => {
  const muiTheme = createTheme();
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const getDefaultChannels = () => {
    if (menuChannels && menuChannels.includes('ECOMM')) {
      return ['ECOMM'];
    }
    return [];
  };

  const handleSelectChannels = (event: any, newValue: any) => {
    setSelectedChannels(newValue);
  };

  const handlePublish = () => {
      onPublish();
      handleCloseModal();
      setOpenConfirmationModal(false);
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedChannels(['ECOMM']);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
    setSelectedChannels(['ECOMM']);
  };

  const handleConfirmationPublish = () => {
    setOpenConfirmationModal(true);
    onClose();
  };

  const getConfirmationData = () => {
    return Array.from(
      new Set(
        selectedStores.map((store) => {
          const marketNo =
            (store.additionalProperties && store.additionalProperties.marketNo) ||
            (store.type === 'store' && store.parents?.org) ||
            '';
          return marketNo;
        })
      )
    )
      .filter(Boolean)
      .map((marketNo) => {
        const storeCount = selectedStores.filter((store) => {
          const storeMarketNo =
            (store.additionalProperties && store.additionalProperties.marketNo) ||
            (store.type === 'store' && store.parents?.org) ||
            '';
          return storeMarketNo === marketNo;
        }).length;

        return {
          marketNo,
          storeCount,
        };
      });
  };

  return (
    <>
      <Modal
        id='publishMenusModal'
        open={open}
        title='Select Menu Channels'
        onClose={onClose}
        data-testid='publishMenusModal'
      >
        <div>
          <Message style={{ marginBottom: rem(16) }}>
            Select Menu Channels to publish YUM Admin menus as inactive for {selectedStores.length} stores.
          </Message>
          <ThemeProvider theme={muiTheme}>
            <Autocomplete
              data-testid='menuChannelsAutocomplete'
              multiple={true}
              disablePortal={false}
              size={'small'}
              options={menuChannels}
              defaultValue={getDefaultChannels()}
              renderInput={(params) => <TextField {...params} label='Select Menu Channels' />}
              onChange={handleSelectChannels}
            />
          </ThemeProvider>
          <Buttons style={{ paddingTop: '16px' }}>
            <Button testId='closeModalButton' variant={Variant.Secondary} label='Cancel' onClick={handleCloseModal} />
            <Button
              variant={Variant.Primary}
              label='Publish'
              onClick={handleConfirmationPublish}
              isLoading={isLoading}
              testId='confirmPublishButton'
              disabled={selectedChannels.length === 0}
            />
          </Buttons>
        </div>
      </Modal>
      <Modal
        id='publishMenusSuccessModal'
        open={openConfirmationModal}
        title='Publish Menus'
        onClose={() => setOpenConfirmationModal(false)}
        data-testid='publishMenusSuccessModal'
      >
        <div>
          <MessagesWrapper>
            <Message style={{ marginBottom: rem(16) }}>
              Are you sure you want to publish YUM Admin menus as inactive for {selectedStores.length} stores?
              <StyledSubMessage>
                {' '}
                Selected menu channels: <StyledMessage>{selectedChannels.join(', ')}</StyledMessage>
              </StyledSubMessage>
            </Message>
          </MessagesWrapper>
          <Table
            rowData={getConfirmationData()}
            returnedRecords={selectedStores.length}
            columnDefs={[
              {
                headerName: 'Market No',
                field: 'marketNo',
                sortable: true,
                filter: true,
              },
              {
                headerName: 'Number of Stores in Market',
                field: 'storeCount',
                sortable: true,
              },
            ]}
          />
          <Buttons style={{ paddingTop: '16px' }}>
            <Button variant={Variant.Secondary} label='CANCEL' onClick={handleCloseConfirmationModal} />
            <Button variant={Variant.Primary} label='CONFIRM' onClick={handlePublish} />
          </Buttons>
        </div>
      </Modal>
    </>
  );
};

export default PublishMenusModal;
